// src/pages/reports/MenuReports.jsx
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import useRestaurantContext from "../../hooks/useRestaurantContext";
import {
  ReportCard,
  ReportFilterBar,
  ReportTable,
} from "../../components/reports";

const defaultDateRange = { preset: "7d", start: "", end: "" };

// ---------- helpers ----------

const getOrderDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const resolveDateRange = (dateRange) => {
  const now = new Date();
  let start = null;
  let end = null;

  if (dateRange.preset === "today") {
    start = new Date();
    start.setHours(0, 0, 0, 0);
    end = new Date(start);
    end.setDate(end.getDate() + 1);
  } else if (dateRange.preset === "7d") {
    end = new Date(now);
    end.setHours(23, 59, 59, 999);
    start = new Date(end);
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
  } else if (dateRange.preset === "30d") {
    end = new Date(now);
    end.setHours(23, 59, 59, 999);
    start = new Date(end);
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
  } else if (dateRange.preset === "custom") {
    if (dateRange.start) {
      start = new Date(dateRange.start);
      start.setHours(0, 0, 0, 0);
    }
    if (dateRange.end) {
      end = new Date(dateRange.end);
      end.setHours(23, 59, 59, 999);
    }
  }

  return { start, end };
};

const parseNumber = (v, fallback = 0) => {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isNaN(n) ? fallback : n;
  }
  return fallback;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(parseNumber(value));

// ---------- component ----------

const MenuReports = () => {
  const { restaurantId, loading: restaurantLoading } =
    useRestaurantContext() || {};

  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [ordersDocs, setOrdersDocs] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // 1) Load orders from the root orders collection only
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        if (!restaurantId) {
          console.warn("[MenuReports] No restaurantId yet, skipping fetch");
          setOrdersDocs([]);
          setLoading(false);
          return;
        }

        const ordersRef = collection(db, "orders");
        const ordersQuery = query(
          ordersRef,
          where("restaurantId", "==", restaurantId)
        );

        const snap = await getDocs(ordersQuery);

        const rows = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        console.log("[MenuReports] loaded orders:", rows.length);
        if (rows[0]) {
          console.log("[MenuReports] sample order:", rows[0]);
        }

        setOrdersDocs(rows);
      } catch (err) {
        console.error("[MenuReports] Load orders failed:", err);
        setOrdersDocs([]);
      } finally {
        setLoading(false);
      }
    };

    if (restaurantLoading) {
      setLoading(true);
      return;
    }

    fetchOrders();
  }, [restaurantId, restaurantLoading]);

  // 2) Load menuItems for this restaurant (for categories)
  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        if (!restaurantId) {
          setMenuItems([]);
          return;
        }
        const snap = await getDocs(
          collection(db, "restaurants", restaurantId, "menuItems")
        );
        const rows = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        console.log("[MenuReports] loaded menuItems:", rows.length);
        if (rows[0]) {
          console.log("[MenuReports] sample menuItem:", rows[0]);
        }
        setMenuItems(rows);
      } catch (err) {
        console.error("[MenuReports] Load menuItems failed:", err);
        setMenuItems([]);
      }
    };

    if (restaurantLoading) {
      return;
    }

    fetchMenuItems();
  }, [restaurantId, restaurantLoading]);

  // 3) Category map by id
  const categoryMap = useMemo(() => {
    const map = new Map();
    menuItems.forEach((item) => {
      const idRaw =
        item.id ??
        item.itemId ??
        item.menuItemId ??
        item.menuItemID;
      const id = idRaw != null ? String(idRaw) : null;

      const cat =
        item.categoryName ||
        item.category ||
        item.categoryLabel ||
        item.categoryTitle ||
        "";

      if (id && cat) {
        map.set(id, cat);
      }
    });
    console.log("[MenuReports] categoryMap size:", map.size);
    return map;
  }, [menuItems]);

  // 4) Extract "line items" for this restaurant + date range
  const lineItems = useMemo(() => {
    const { start, end } = resolveDateRange(dateRange);
    const result = [];

    const pushIfValid = (rawDoc, candidate, sourceKey) => {
      if (!candidate || typeof candidate !== "object") return;

      // restaurant filter – try several possibilities
      const lineRestaurantId =
        candidate.restaurantId ||
        candidate.restaurantID ||
        rawDoc.restaurantId ||
        rawDoc.restaurantID;

      if (restaurantId && lineRestaurantId && lineRestaurantId !== restaurantId)
        return;

      // date filter – try multiple fields, but don’t drop if none exist
      const d = getOrderDate(
        candidate.createdAt ||
          candidate.placedAt ||
          candidate.orderTime ||
          candidate.orderDate ||
          rawDoc.createdAt ||
          rawDoc.placedAt ||
          rawDoc.orderTime ||
          rawDoc.orderDate
      );

      if (start || end) {
        if (d) {
          if (start && d < start) return;
          if (end && d > end) return;
        }
        // If no date, we keep it anyway
      }

      result.push({ __source: sourceKey, ...candidate });
    };

    const traverse = (rawDoc, node, sourceKey) => {
      if (!node || typeof node !== "object") return;

      // Treat this node itself as a possible line item
      pushIfValid(rawDoc, node, sourceKey);

      Object.entries(node).forEach(([key, value]) => {
        if (!value) return;
        const nextKey = sourceKey ? `${sourceKey}.${key}` : key;
        if (Array.isArray(value)) {
          value.forEach((v, idx) =>
            traverse(rawDoc, v, `${nextKey}[${idx}]`)
          );
        } else if (typeof value === "object") {
          traverse(rawDoc, value, nextKey);
        }
      });
    };

    ordersDocs.forEach((doc) => {
      const data = { ...doc };
      delete data.id;
      delete data.source;

      const rootLooksLikeItem =
        data.name ||
        data.itemName ||
        data.title ||
        data.category ||
        data.categoryName;

      if (rootLooksLikeItem) {
        pushIfValid(doc, data, "root");
      }

      traverse(doc, data, "");
    });

    console.log("[MenuReports] line items for stats:", result.length);
    if (result[0]) {
      console.log("[MenuReports] sample line item:", result[0]);
    }
    return result;
  }, [ordersDocs, restaurantId, dateRange]);

  // 5) Aggregate per-item stats (using categoryMap + nameCategoryMap)
  const itemStats = useMemo(() => {
    const map = new Map();

    // name -> category from menuItems
    const nameCategoryMap = new Map();
    menuItems.forEach((mi) => {
      const cat =
        mi.categoryName ||
        mi.category ||
        mi.categoryLabel ||
        mi.categoryTitle ||
        "";
      const nameKey = (mi.name || mi.itemName || "")
        .toString()
        .trim()
        .toLowerCase();
      if (nameKey && cat && !nameCategoryMap.has(nameKey)) {
        nameCategoryMap.set(nameKey, cat);
      }
    });

    lineItems.forEach((line) => {
      const menuItemIdRaw =
        line.menuItemId ??
        line.menuItemID ??
        line.itemId ??
        line.menuItem ??
        line.menu_item_id;
      const menuItemId = menuItemIdRaw != null ? String(menuItemIdRaw) : null;

      const rawName =
        line.name || line.itemName || line.title || "";

      let rawCategory =
        line.category ||
        line.categoryName ||
        line.categoryLabel ||
        "";

      if (!rawCategory && menuItemId && categoryMap.has(menuItemId)) {
        rawCategory = categoryMap.get(menuItemId);
      }

      if (!rawName && !rawCategory) return;

      const itemName = rawName || "Unknown item";
      let category = rawCategory || "Uncategorized";

      const qty =
        parseNumber(line.quantity, 1) ||
        parseNumber(line.qty, 1) ||
        1;

      const price =
        parseNumber(line.price) ||
        parseNumber(line.unitPrice) ||
        parseNumber(line.totalAmount);

      const key =
        menuItemId ||
        (line.id != null ? String(line.id) : null) ||
        `${itemName}-${category}`;

      const prev =
        map.get(key) || {
          id: key,
          name: itemName,
          category,
          orders: 0,
          qty: 0,
          sales: 0,
        };

      // Prefer any real category over "Uncategorized"
      const mergedCategory =
        prev.category && prev.category !== "Uncategorized"
          ? prev.category
          : category;

      map.set(key, {
        ...prev,
        category: mergedCategory,
        orders: prev.orders + 1,
        qty: prev.qty + qty,
        sales: prev.sales + price * qty,
      });
    });

    let arr = [...map.values()];

    // Second pass: fix "Uncategorized" using id and name
    arr = arr.map((item) => {
      let category = item.category;
      const id = item.id != null ? String(item.id) : null;

      if (
        id &&
        category === "Uncategorized" &&
        categoryMap.has(id)
      ) {
        category = categoryMap.get(id);
      }

      if (category === "Uncategorized") {
        const nameKey = item.name
          ? item.name.toString().trim().toLowerCase()
          : "";
        if (nameKey && nameCategoryMap.has(nameKey)) {
          category = nameCategoryMap.get(nameKey);
        }
      }

      return { ...item, category };
    });

    return arr;
  }, [lineItems, categoryMap, menuItems]);

  // 6) KPIs
  const kpis = useMemo(() => {
    const uniqueItems = itemStats.length;
    const totalQty = itemStats.reduce((s, i) => s + (i.qty || 0), 0);

    const sortedByQty = itemStats.slice().sort((a, b) => b.qty - a.qty);
    const top = sortedByQty[0];
    const low = sortedByQty.filter((i) => i.qty > 0).slice(-1)[0];

    return {
      uniqueItems,
      totalQty,
      topName: top ? top.name : "—",
      lowName: low ? low.name : "—",
    };
  }, [itemStats]);

  // 7) Tables
  const topItems = useMemo(
    () =>
      itemStats
        .slice()
        .sort((a, b) => b.qty - a.qty)
        .slice(0, 15),
    [itemStats]
  );

  const lowItems = useMemo(
    () =>
      itemStats
        .filter((i) => i.qty > 0)
        .slice()
        .sort((a, b) => a.qty - b.qty)
        .slice(0, 15),
    [itemStats]
  );

  if (restaurantLoading) {
    return (
      <div className="reports-section">
        <p className="muted">Loading restaurant…</p>
      </div>
    );
  }

  if (!restaurantId) {
    return (
      <div className="reports-section">
        <p className="muted">No restaurant found. Please sign in again.</p>
      </div>
    );
  }

  // ---------- render ----------

  return (
    <div className="reports-section">
      <ReportFilterBar
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        grouping={null}
        onGroupingChange={null}
      />

      <section className="reports-kpi-grid">
        <ReportCard
          title="Unique items sold"
          value={kpis.uniqueItems}
        />
        <ReportCard
          title="Total quantity sold"
          value={kpis.totalQty}
        />
        <ReportCard
          title="Top-selling item"
          value={kpis.topName}
        />
        <ReportCard
          title="Lowest-selling item"
          value={kpis.lowName}
          subtitle="Among items that sold at least once"
        />
      </section>

      <section className="reports-row">
        <div className="reports-column">
          <h3 className="reports-section-title">Top dishes</h3>
          <ReportTable
            columns={[
              { title: "Item", dataIndex: "name", key: "name" },
              { title: "Category", dataIndex: "category", key: "category" },
              { title: "Orders", dataIndex: "orders", key: "orders" },
              { title: "Qty sold", dataIndex: "qty", key: "qty" },
              {
                title: "Sales",
                dataIndex: "sales",
                key: "sales",
                render: (v) => formatCurrency(v),
              },
            ]}
            data={topItems}
          />
        </div>

        <div className="reports-column">
          <h3 className="reports-section-title">Low sellers</h3>
          <ReportTable
            columns={[
              { title: "Item", dataIndex: "name", key: "name" },
              { title: "Category", dataIndex: "category", key: "category" },
              { title: "Orders", dataIndex: "orders", key: "orders" },
              { title: "Qty sold", dataIndex: "qty", key: "qty" },
              {
                title: "Sales",
                dataIndex: "sales",
                key: "sales",
                render: (v) => formatCurrency(v),
              },
            ]}
            data={lowItems}
          />
        </div>
      </section>

      {loading && (
        <div className="reports-loading">Loading data…</div>
      )}
    </div>
  );
};

export default MenuReports;
